import { FC } from "react";
import { ChannelHoldout } from "@models/population.model";
import { networkLabel } from "@/shared/enums/networkColors";
import Disclosure from "./Disclosure";
import StepPanel, { sumSteps } from "./StepPanel";

/*
    Conversions by step, tiered (MPN-2077): one collapsible per applied channel holdout, its
    two arms side by side in the same panel the Treatment and Held Out pair uses above it.

    Both arms are a subset of Treatment. The channel treatment is served on the channel; the
    channel holdout is not, and gets the same mix of every other channel. So the two panels
    add up to the Treatment panel, which is what the tag in the title says, and each closes
    with a Total row because there are no stat tiles for this tier.

    These two count from the day the channel test started, not from the campaign's go-live
    date (MPN-2183), so the header says which day that is. A channel added mid-campaign --
    Penn Foster's Vibe, on a campaign running since September -- otherwise showed months of
    conversions it could not have caused, in both arms. They read as pre-converts here.
    The Treatment and Held Out panels above are unchanged.
*/
const n = (value: number) => value.toLocaleString();

/** Date-only string, read as local midnight so it does not slip a day in a western zone. */
const day = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/** "50 / 50" — treatment first, holdout second, the way Ops says it. */
export const splitLabel = (pct: number) => `${100 - pct} / ${pct}`;

const TieredConversions: FC<{ holdout: ChannelHoldout }> = ({ holdout }) => {
  const exposedSteps = holdout.conversions_by_step_exposed;
  const heldOutSteps = holdout.conversions_by_step_held_out;
  // An older server sends no arms; then there is nothing to show, not a panel of zeros.
  if (exposedSteps === undefined || heldOutSteps === undefined) return null;

  const label = holdout.label || networkLabel(holdout.channel);
  const exposed = holdout.treatment_count - holdout.held_out_count;
  const heldOut = holdout.held_out_count;

  return (
    <Disclosure
      title="Conversions by step — tiered"
      defaultOpen
      className="population-disclosure--tiered"
      tags={
        <>
          <span className="population-tag">{label}</span>
          <span className="population-tag population-tag--muted">
            Subset of Treatment · {n(holdout.treatment_count)} members, {splitLabel(holdout.pct)}
          </span>
          {holdout.channel_start && (
            <span className="population-tag population-tag--muted">
              Since {day(holdout.channel_start)}
            </span>
          )}
        </>
      }
      summary={`${n(exposed)} channel treatment · ${n(heldOut)} channel holdout`}
    >
      <div className="population-steps-pair">
        <StepPanel
          title="Conversions by step — Channel treatment"
          tag={label}
          steps={exposedSteps}
          total={{
            members: exposed,
            converters: holdout.converters_exposed ?? 0,
            ...sumSteps(exposedSteps),
          }}
        />
        <StepPanel
          title="Conversions by step — Channel holdout"
          tag={label}
          steps={heldOutSteps}
          holdout
          total={{
            members: heldOut,
            converters: holdout.converters_held_out ?? 0,
            ...sumSteps(heldOutSteps),
          }}
        />
      </div>
    </Disclosure>
  );
};

export default TieredConversions;
